/* THE PANEL REGISTRY — one list, every shell reads it.
 *
 * ═══ WHAT THIS IS FOR ═══
 *
 * Adding a panel to the hosted chart currently costs four edits in four files:
 * a `MobileSheet` mounted in App.svelte with its own peek snippet, an id added
 * to the `SheetId` union in mobile.svelte.ts, a slot added to Dock.svelte with
 * a re-check that the row still fits 393 px, and a separate desktop branch that
 * has to pick an absolute position and hope it does not land on the tool
 * column. Three of those four are about layout and none of them is the feature.
 *
 * It is also why `Layers.svelte` carries an `inSheet` prop, `ChartTools.svelte`
 * carries a `variant` prop, and `RouteContent.svelte` exists as a file of its
 * own — three ad-hoc answers to one missing abstraction.
 *
 * Here a panel is a value. The rail, the dock and the sheet all render from
 * this list, so they cannot disagree about what exists, what it is called, or
 * whether it is available.
 *
 * ═══ THE HOST OWNS THE PANELS, THIS OWNS THE ARRANGEMENT ═══
 *
 * The definitions come from the app — apps/web's Trip panel knows about
 * autoroute, which chart-ui may not import. What lives here is the part that is
 * the same everywhere: what is registered, what order it is in, which one is
 * up, and how tall it is resting. That is the same seam `configureSources` and
 * `configurePassage` already use.
 */
import type { Component, Snippet } from 'svelte';
import type { IconName } from '../primitives/Icon.svelte';

/** Where a sheet rests. TWO, not three.
 *
 *  Google Maps and Apple both ship peek / medium / large and this had all
 *  three. The deciding fact was local: every detent is already capped at the
 *  measured height of its content, and the panels this was built for are
 *  shorter than half of a 660 px screen — so `half` and `full` resolved to the
 *  same number and the third detent was a state the user could reach and could
 *  not tell apart from the second. A tap that visibly does nothing reads as a
 *  broken control. */
export type Detent = 'peek' | 'open';

export interface PanelDef {
  id: string;
  /** Shown as the panel's heading on a desktop, and as the sheet's accessible
   *  name on a phone. */
  title: string;
  /** The mark in the rail and the dock. A name from the design system's own
   *  set — see `primitives/Icon.svelte`, which is the only file that knows an
   *  icon library exists.
   *
   *  The icon and the running order are EDITORIAL judgements: deriving them
   *  from the registry would put every new panel in the navigation
   *  automatically and nobody would then be making them. */
  icon: IconName;

  /** ═══ A LIVE READING, SHOWN INSTEAD OF THE ICON ═══
   *
   *  Two entries need this and both for the same reason: the STATE is more
   *  useful than the symbol, and it is state you must not have to open a panel
   *  to see.
   *
   *  The time entry shows the cursor's reading — a parked cursor means every
   *  tide bar, stream arrow and current field on the chart behind it is drawn
   *  for another moment. The tools entry shows the armed tool's label while one
   *  is armed — "TAP TWO POINTS" is not something a chart can imply, and a mode
   *  with no indicator is a mode you cannot see.
   *
   *  Return null to fall back to the icon. Nielsen Norman are explicit that
   *  always-needed state must not live behind a disclosure, which is also what
   *  `pin` is for. */
  readout?: () => string | null;

  /** Colours the nav entry. `warn` is the parked cursor and nothing else —
   *  amber is this app's one word for "this is not now". Left alone, an open
   *  panel is teal and a shut one is neutral, which the shells handle
   *  themselves. */
  tone?: () => 'normal' | 'warn';

  /** ═══ WHEN THE ENTRY IS AN ACTION, NOT A DESTINATION ═══
   *
   *  Called before the panel would open. Return `true` to say the tap was
   *  handled and the panel should stay shut.
   *
   *  One entry needs this and it earns it: while a tool is ARMED, the tools
   *  entry stops being a way in and becomes that tool's live label — "TAP TWO
   *  POINTS" — and tapping it stands the tool down. An armed ruler with no
   *  indicator is a mode you cannot see, and standing it down should not
   *  require finding the row that armed it. It is the one thing the old
   *  floating tool row genuinely did well.
   *
   *  Use it for that and nothing else. An entry that sometimes opens a panel
   *  and sometimes does something else is a control people have to watch to
   *  understand, which is only worth it when the alternative is a mode with no
   *  way out. */
  onactivate?: () => boolean | void;

  /** ═══ NEVER FOLDED INTO THE DOCK'S OVERFLOW ═══
   *
   *  The phone's dock holds five slots and folds the tail into MORE. That is
   *  right for panels and wrong for STATE: the time entry's glyph is the
   *  cursor's reading and its tone is whether the chart is showing now, and a
   *  parked cursor means every tide bar, stream arrow and current field behind
   *  it is drawn for another moment. Nielsen Norman are explicit that
   *  always-needed state must not live behind a disclosure.
   *
   *  Registering a sixth panel is what caught this: the time slot went quietly
   *  into MORE and the phone lost its only indication that the chart was an
   *  hour and twenty minutes ahead of itself.
   *
   *  ═══ A GETTER, BECAUSE ONE PIN IS CONDITIONAL ═══
   *
   *  The time entry pins always. The TOOLS entry pins only while a tool is
   *  armed — and then it must, for the same reason: "TAP TWO POINTS" is a mode
   *  the chart is in, and a mode whose only indicator is behind a MORE menu is
   *  a mode you cannot see. Unarmed it is an ordinary destination and can
   *  overflow like any other.
   *
   *  Caught by `_mobile-proof.mjs`: with nine panels registered the tools entry
   *  fell into the overflow, and arming the ruler left the phone with no sign
   *  anywhere that the next tap would drop a measuring point.
   *
   *  Use sparingly. Every pin costs one of the five slots. */
  pin?: boolean | (() => boolean);
  /** Running order in the rail and the dock. Sparse on purpose (10, 20, 30…) so
   *  a panel can be slotted between two without renumbering the file. */
  order: number;
  /** The body. Rendered inside `<Panel>`, which supplies the chrome. */
  content: Component<Record<string, never>>;

  /** ═══ THE ACTION, PINNED BELOW THE SCROLL ═══
   *
   *  Separate from `content` on purpose. The one action a panel exists to offer
   *  — "Save trip", "Use 06:40 Sunday" — must not scroll off the bottom of a
   *  schedule nobody thought to scroll, and leaving it as the last thing in the
   *  body is exactly how the desktop route box put EXPORT GPX below the fold at
   *  `max-height: calc(100dvh - 190px)`.
   *
   *  Optional, and most panels have none: Layers and Search are things you
   *  operate, not things you commit. A panel with a foot is a panel that
   *  produces something. */
  foot?: Component<Record<string, never>>;

  /** ═══ THE ANSWER, NOT THE TITLE ═══
   *
   *  A sheet that says "TRIP" at peek has spent its 96 px telling you what you
   *  just tapped. This carries the fact the panel exists to deliver — "hold
   *  1h04 at Dodd · arrive 11:56", "7 of 14 layers on" — so the sheet is useful
   *  without being opened at all, which is the whole reason a peek detent is
   *  worth having.
   *
   *  A getter rather than a string: these change under the user's hand, and a
   *  value captured at registration would be wrong by the time it was read. */
  peek?: () => string;

  /** In the rail and the dock. False for panels that open in RESPONSE to
   *  something rather than being navigated to — the object inspector opens
   *  because you tapped the chart, and a permanent button for it would be a
   *  button that does nothing until it does. */
  nav?: boolean;

  /** Marked unavailable while false, with its reason shown when it is opened. */
  available?: () => boolean;

  /** Why it is unavailable, for its nav entry (rail/dock title and
   *  accessible name, joined as "<title> — <reason>"). Keep this to the
   *  STATE only — never how-to instructions: #120.
   *
   *  The fuller how-to lives in `detail` and renders in the Empty
   *  explanation, one tap away. The same rule the layers panel follows: a
   *  control that silently does nothing is worse than one that says why. */
  reason?: () => string | null;

  /** The plain-words how-to behind the `reason`, rendered by `Empty` as the
   *  explanation body when the unavailable panel is opened. Optional — when
   *  absent the body falls back to `reason`. #120. */
  detail?: () => string | null;

  /** A single way out of the unavailable state, rendered by `Empty`. */
  unavailableAction?: Snippet;
}

class Panels {
  /* `$state.raw` because the definitions are frozen values — a def is replaced,
   * never mutated — and deep proxying every `content` component and closure for
   * no reason is a cost paid on every read. */
  #defs = $state.raw<PanelDef[]>([]);

  /** Which panel is up, or null. On a phone this is the ONE surface and exactly
   *  one may be up at a time; on a desktop it is which one the panel column is
   *  showing. That constraint is a fact about the app rather than a negotiation
   *  between siblings, which is why it lives here. */
  open = $state<string | null>(null);

  /** Its resting position. Only meaningful on a phone. */
  detent = $state<Detent>('peek');

  register(def: PanelDef): () => void {
    /* Replace-by-id rather than append. A component that registers in an effect
     * re-registers when its dependencies change, and the first version of this
     * grew four copies of the Layers entry in the rail before anybody noticed —
     * because a duplicate looks exactly like an intentional second entry. */
    this.#defs = [...this.#defs.filter((d) => d.id !== def.id), def].sort(
      (a, b) => a.order - b.order
    );
    return () => this.unregister(def.id);
  }

  unregister(id: string): void {
    this.#defs = this.#defs.filter((d) => d.id !== id);
    if (this.open === id) this.open = null;
  }

  /** Everything registered, in order. Unavailable panels are INCLUDED so the
   *  user can open one and read why it is unavailable. */
  list(): readonly PanelDef[] {
    return this.#defs;
  }

  /** The ones the rail and the dock draw. */
  nav(): readonly PanelDef[] {
    return this.#defs.filter((d) => d.nav !== false);
  }

  get(id: string): PanelDef | null {
    return this.#defs.find((d) => d.id === id) ?? null;
  }

  /** The def currently up, if it is still registered and still available. Both
   *  checks matter: a panel can go away underneath an open sheet — and rendering
   *  a def that has said it is unavailable is how a panel comes back empty.
   *  AppShell no longer uses this: an unavailable-but-open panel keeps the
   *  shell so its Empty explanation stays visible, via `unavailablePanel`. */
  current(): PanelDef | null {
    const d = this.open === null ? null : this.get(this.open);
    if (!d) return null;
    return d.available?.() === false ? null : d;
  }

  isAvailable(id: string): boolean {
    return this.get(id)?.available?.() !== false;
  }

  /**
   * Put a panel up.
   *
   * `at` is where it should REST, and the default is deliberate: a panel the
   * user asked for by tapping a dock slot OPENS, because they went looking for
   * it. A panel that appears because something HAPPENED — a route solved —
   * rests at peek, because the chart is what they were looking at and the
   * answer fits on the one line the peek shows.
   */
  show(id: string, at: Detent = 'open'): void {
    this.open = id;
    this.detent = at;
  }

  close(id?: string): void {
    if (id && this.open !== id) return;
    this.open = null;
  }

  /** What a rail button or a dock slot does when tapped. Honours
   *  `onactivate` — see the note on it. */
  toggle(id: string, at: Detent = 'open'): void {
    if (this.get(id)?.onactivate?.() === true) return;
    if (this.open === id) this.close(id);
    else this.show(id, at);
  }

  is(id: string): boolean {
    return this.open === id;
  }
}

export const panels = new Panels();

export function unavailablePanel(
  panel: PanelDef | null
): { title: string; body: string; action: Snippet | undefined } | null {
  if (!panel || panel.available?.() !== false) return null;
  return {
    title: panel.title,
    body: panel.detail?.() ?? panel.reason?.() ?? 'Not available right now.',
    action: panel.unavailableAction
  };
}
